import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import {
  Figure,
  LoadMore,
  NameLink,
  PersonScopeCard,
  TABLE_REGION,
  TD,
  TD_MUTED,
  TD_NUM,
  TH,
  TH_NUM,
  THEAD,
  caseHref,
  partyHref,
  useMoney,
} from '@/pages/cvm/shared';
import { cvmAppealParams, fetchCvmAppeals } from '@/services/cvm';
import type { CvmAppealRow } from '@/services/types';

const PAGE_SIZE = 50;
const OUTCOMES = ['DENIED', 'GRANTED', 'PARTIAL', 'NOT_ADMITTED'] as const;

/**
 * The second instance: CRSFN acórdãos on CVM-origin processes.
 *
 * One row per appellant, with the outcome the dispositivo states and, where
 * it restates the sanction, what became of it. The two mix panels count only
 * rows whose dispositivo the parser read; the shape panel says how many did
 * not, and those rows are listed with a blank outcome rather than dropped. A
 * CVM ex officio appeal is CVM appealing its own acquittal, and the role
 * column says so.
 */
export function CvmAppealsPage() {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('q') ?? '');
  const [q, setQ] = useState<string | null>(seed.get('q') || null);
  const [outcome, setOutcome] = useState(seed.get('outcome') ?? '');
  const filters = { q, outcome: outcome || null };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['cvm', 'appeals', filters],
    queryFn: ({ pageParam }) =>
      fetchCvmAppeals({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cvm:appeals.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:appeals.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:appeals.explainer" />
      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {data ? (
        <>
          <section className="rounded-lg border bg-card p-4" data-shape>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('cvm:appeals.shape.title')}
            </h2>
            <Answer answer={data.shape} help={t('cvm:appeals.shape.help')}>
              {(s) => (
                <>
                  <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <Figure label={t('cvm:appeals.shape.rows')} value={number.format(s.rows)} />
                    <Figure
                      label={t('cvm:appeals.shape.withOutcome')}
                      value={number.format(s.with_outcome)}
                    />
                    <Figure
                      label={t('cvm:appeals.shape.withSanctionAfter')}
                      value={number.format(s.with_sanction_after)}
                    />
                    <Figure
                      label={t('cvm:appeals.shape.withFineAfter')}
                      value={number.format(s.with_fine_after)}
                    />
                  </dl>
                  <dl className="mt-2 grid grid-cols-3 gap-2">
                    <Figure
                      label={t('cvm:appeals.shape.linked')}
                      value={number.format(s.linked_to_judgment)}
                    />
                    <Figure
                      label={t('cvm:appeals.shape.exOfficio')}
                      value={number.format(s.cvm_ex_officio)}
                    />
                    <Figure
                      label={t('cvm:appeals.shape.sessions')}
                      value={number.format(s.sessions)}
                      hint={
                        s.first_session && s.last_session
                          ? t('cvm:appeals.shape.sessionSpan', {
                              first: s.first_session,
                              last: s.last_session,
                            })
                          : undefined
                      }
                    />
                  </dl>
                </>
              )}
            </Answer>
          </section>

          <div className="grid gap-4 lg:grid-cols-2">
            <OutcomeMixPanel
              answer={data.outcome_mix}
              titleKey="cvm:appeals.outcomeMix.title"
              helpKey="cvm:appeals.outcomeMix.help"
              labelPrefix="cvm:appealOutcomes."
            />
            <OutcomeMixPanel
              answer={data.sanction_after}
              titleKey="cvm:appeals.sanctionAfter.title"
              helpKey="cvm:appeals.sanctionAfter.help"
              labelPrefix="cvm:sanctionAfter."
            />
          </div>
        </>
      ) : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQ(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="cvm-appeal-query" className="text-xs font-medium text-foreground">
            {t('cvm:appeals.search.label')}
          </label>
          <input
            id="cvm-appeal-query"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('cvm:appeals.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="cvm-appeal-outcome" className="block text-xs font-medium text-foreground">
            {t('cvm:appeals.filters.outcome')}
          </label>
          <select
            id="cvm-appeal-outcome"
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
            className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('cvm:appeals.filters.any')}</option>
            {OUTCOMES.map((code) => (
              <option key={code} value={code}>
                {t(`cvm:appealOutcomes.${code}`)}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('cvm:appeals.search.submit')}
        </button>
        <div className="ml-auto">
          <ExportButton
            path={`/api/v1/cvm/export/appeals?${cvmAppealParams(filters).toString()}`}
          />
        </div>
      </form>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : !data ? (
        <SkeletonLoader rows={8} />
      ) : data.total === 0 ? (
        <EmptyState hint={t('cvm:list.empty')} />
      ) : (
        <>
          <p
            className="text-xs text-muted-foreground"
            aria-live="polite"
            data-total={data.total}
            data-stale={isPlaceholderData ? 'true' : 'false'}
          >
            {t('cvm:list.showing', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>
          <div
            ref={scrollRef}
            className={TABLE_REGION}
            role="region"
            aria-label={t('cvm:appeals.tableLabel')}
          >
            <table className="w-full text-sm">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>{t('cvm:appeals.columns.session')}</th>
                  <th className={TH}>{t('cvm:appeals.columns.process')}</th>
                  <th className={TH}>{t('cvm:appeals.columns.party')}</th>
                  <th className={TH}>{t('cvm:appeals.columns.role')}</th>
                  <th className={TH}>{t('cvm:appeals.columns.outcome')}</th>
                  <th className={TH}>{t('cvm:appeals.columns.sanctionAfter')}</th>
                  <th className={TH_NUM}>{t('cvm:appeals.columns.fineAfter')}</th>
                  <th className={TH}>{t('cvm:appeals.columns.relator')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, index) => (
                  <AppealRow key={`${row.page_url}-${row.party_slug}-${index}`} row={row} />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} aria-hidden className="h-px" />
            <LoadMore
              hasNextPage={Boolean(hasNextPage)}
              isFetchingNextPage={isFetchingNextPage}
              loadMore={loadMore}
              total={data.total}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}

      {data ? <PersonScopeCard answer={data.person_scope} /> : null}
      <FieldGuide i18nKey="cvm:appeals.guide" />
    </div>
  );
}

function AppealRow({ row }: { row: CvmAppealRow }) {
  const { t } = useTranslation(['cvm']);
  const money = useMoney();
  return (
    <tr className="border-t align-top hover:bg-muted/30">
      <td className={TD_MUTED}>
        <span className="text-foreground">
          {row.session_number ? t('cvm:case.appeals.session', { number: row.session_number }) : '—'}
        </span>
        {row.acordao_number ? <div>{row.acordao_number}</div> : null}
        <div>
          <a
            href={row.page_url}
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline"
          >
            {t('cvm:case.appeals.page')}
          </a>
        </div>
      </td>
      <td className={TD}>
        {row.case_key ? (
          <Link
            to={caseHref(row.case_key)}
            className="text-primary underline-offset-2 hover:underline"
          >
            {row.process_number ?? row.process_key}
          </Link>
        ) : (
          <span className="text-muted-foreground">
            {row.process_key ?? row.crsfn_process_number ?? '—'}
          </span>
        )}
        {row.judgment_date ? (
          <div className="text-xs text-muted-foreground">{row.judgment_date}</div>
        ) : null}
        {row.crsfn_process_number ? (
          <div className="text-xs text-muted-foreground">{row.crsfn_process_number}</div>
        ) : null}
      </td>
      <td className={TD}>
        <NameLink name={row.party} href={partyHref(row.party_slug)} />
        {row.entity_type ? (
          <div className="text-xs text-muted-foreground">
            {t(`cvm:entityTypes.${row.entity_type}`, {
              defaultValue: row.entity_type,
            })}
          </div>
        ) : null}
      </td>
      <td className={TD_MUTED}>{t(`cvm:appealRoles.${row.role}`, { defaultValue: row.role })}</td>
      <td className={TD}>
        {row.outcome ? (
          t(`cvm:appealOutcomes.${row.outcome}`, { defaultValue: row.outcome })
        ) : (
          <span className="text-muted-foreground">{t('cvm:case.appeals.unread')}</span>
        )}
        {row.is_unanimous == null ? null : (
          <div className="text-xs text-muted-foreground">
            {row.is_unanimous
              ? t('cvm:case.settlements.unanimous')
              : t('cvm:case.settlements.majority')}
          </div>
        )}
      </td>
      <td className={TD_MUTED}>
        {row.sanction_after
          ? t(`cvm:sanctionAfter.${row.sanction_after}`, {
              defaultValue: row.sanction_after,
            })
          : '—'}
      </td>
      <td className={TD_NUM}>{money(row.fine_after_appeal_brl)}</td>
      <td className={TD_MUTED}>{row.relator ?? '—'}</td>
    </tr>
  );
}
